import { Suspense } from 'react';
import {
  Await,
  defer,
  Link,
  LoaderFunction,
  Navigate,
  useLoaderData,
} from 'react-router-dom';
import { api } from '../../../utils/fetchApi';
import { TaskRef } from '../utils/types';
import { TaskSummaryCard } from '@mosey/components/layout/Card';
import { getTaskDueDate, sortTaskAlerts } from '../utils';
import { TasksOverviewZeroState } from './TasksOverviewZeroState';
import { useUser } from '../../../hooks/useUser';
import { paths } from '@mosey/api-types';
import { TruncatedList } from '@mosey/components/layout/TruncatedList';
import { TaskCollectionCard } from '@mosey/components/layout/TaskCollectionCard';

export const loader: LoaderFunction = async () => {
  const todos = api({
    url: '/api/compliance/tasks?is_setup=false&task_type=requirement&statuses=todo',
    method: 'GET',
  }).then((res) => res.json());
  const setupSummary = api({
    url: '/api/compliance/tasks/summary',
    method: 'GET',
  }).then((res) => res.json());
  const legislation = api({
    url: '/api/legislation',
    method: 'GET',
  }).then((res) => res.json());

  // TODO: Update batch to allow defer
  return defer({
    todos: await todos,
    setupSummary,
    legislation,
  });
};

type SetupSummary =
  paths['/api/compliance/tasks/summary']['get']['responses']['200']['content']['application/json'];

type Legislation =
  paths['/api/legislation']['get']['responses']['200']['content']['application/json'];

export const Component = () => {
  const user = useUser();
  const { todos, setupSummary, legislation } = useLoaderData() as {
    todos: TaskRef[];
    setupSummary: Promise<SetupSummary>;
    legislation: Promise<Legislation>;
  };

  if (user.role !== 'admin' || !user.legal_entity.is_gutz_enabled) {
    return <Navigate to="/home" />;
  }

  return (
    <div className="grid grid-cols-[minmax(0,_1fr)_374px] items-start gap-x-10 px-8 py-6">
      {todos.length > 0 ? (
        <ul className="space-y-2">
          {todos.map((task) => {
            const { id, status, title, source } = task;
            const formattedDueDate = getTaskDueDate(task);

            return (
              <TaskSummaryCard
                as="li"
                key={id}
                status={status}
                title={title}
                description={
                  source.type === 'requirement' ? source.description : undefined
                }
                reason={
                  formattedDueDate
                    ? { title: formattedDueDate, type: 'due' }
                    : undefined
                }
                // TODO: Update this when we have filters
                to={`/resolver/${source.type === 'question' ? 'question' : 'requirement'}/tasks/${id}`}
                region={
                  source.type === 'requirement'
                    ? source.location.name
                    : source.locations[0].name
                }
              />
            );
          })}
        </ul>
      ) : (
        <TasksOverviewZeroState />
      )}

      <aside className="space-y-7">
        <Suspense>
          <Await resolve={setupSummary}>
            {({
              assessment_alerts: assessmentAlerts,
              setup_alerts: setupAlerts,
              in_progress_tasks: inProgressTasks,
            }: SetupSummary) => {
              return (
                <>
                  <TruncatedList
                    title="Complete Setup"
                    body="Setup tasks are based on your responses to questions"
                    itemLabel={
                      setupAlerts.length === 1 ? 'location' : 'locations'
                    }
                  >
                    {setupAlerts.sort(sortTaskAlerts).map((location) => {
                      if (location.region) {
                        return (
                          <TaskCollectionCard
                            key={location.region.code}
                            as="li"
                            title={location.region.name}
                            status="setup-collection"
                            size="medium"
                            to={`/locations/usa/${location.region.code.toLowerCase()}/resolver/setup`}
                            ariaLabelTitle={`${location.region.name} setup`}
                          />
                        );
                      }
                    })}
                  </TruncatedList>

                  <TruncatedList
                    title="Answer Questions"
                    body="Your responses generate setup tasks"
                    itemLabel={
                      assessmentAlerts.length === 1 ? 'location' : 'locations'
                    }
                  >
                    {assessmentAlerts.sort(sortTaskAlerts).map((location) => {
                      if (location.region) {
                        return (
                          <TaskCollectionCard
                            key={location.region.code}
                            as="li"
                            title={location.region.name}
                            status="question-collection"
                            size="medium"
                            to={`/locations/usa/${location.region.code.toLowerCase()}/resolver/assessment`}
                            ariaLabelTitle={`${location.region.name} assessment`}
                          />
                        );
                      }
                    })}
                  </TruncatedList>

                  {inProgressTasks && (
                    <TruncatedList
                      title="Automations in Progress"
                      body="You will be notified if action is required"
                      isStacked
                      itemLabel={
                        inProgressTasks.length === 1
                          ? 'automation'
                          : 'automations'
                      }
                    >
                      {inProgressTasks.map(({ id, title, status }) => {
                        return (
                          <TaskSummaryCard
                            key={id}
                            as="li"
                            title={title}
                            status={status}
                            variant="nav"
                            to={`/tasks/${id}`}
                          />
                        );
                      })}
                    </TruncatedList>
                  )}
                </>
              );
            }}
          </Await>
        </Suspense>

        <Suspense>
          <Await resolve={legislation}>
            {(legislation: Legislation) => {
              return (
                <TruncatedList
                  title="Legislation"
                  body="Tasks will be generated for legislation that applies to you"
                  itemLabel={legislation.length === 1 ? 'update' : 'updates'}
                  isStacked
                >
                  {legislation.map(
                    ({ id, title, effective_date: effectiveDate }) => {
                      return (
                        <Link
                          key={id}
                          className="relative z-0 -mt-px block space-y-0.5 border border-gray-200 px-3 py-[10px] text-sm outline-none last:border-b hover:z-10 hover:border-b hover:border-teal-300 hover:bg-teal-200 focus-visible:z-20 focus-visible:ring-2 focus-visible:ring-rose-700"
                          to={`/legislation#${id}`}
                        >
                          <p className="font-medium">{title}</p>
                          {effectiveDate && <p>Effective {effectiveDate}</p>}
                        </Link>
                      );
                    },
                  )}
                </TruncatedList>
              );
            }}
          </Await>
        </Suspense>
      </aside>
    </div>
  );
};
